import { useEffect, type ReactNode } from 'react'

import { useMeQuery } from '@/api/queries/auth'
import { usePresenceProbe } from '@/features/auth/use-presence-probe'
import { useTokenAutoRefresh } from '@/features/auth/use-token-auto-refresh'
import { handleSessionExpired } from '@/lib/auth-session'
import { useAuthStore } from '@/stores/auth.store'

interface AuthInitProps {
  children: ReactNode
}

export const AuthInit = ({ children }: AuthInitProps) => {
  const accessToken = useAuthStore((state) => state.accessToken)
  const user = useAuthStore((state) => state.user)
  const setUser = useAuthStore((state) => state.setUser)

  const me = useMeQuery(Boolean(accessToken && !user))

  usePresenceProbe()
  useTokenAutoRefresh()

  // Only rehydrate user while a session is still present. After logout /
  // session expiry, me.data may still be in memory until the query is cleared;
  // never write it back over a logged-out store.
  useEffect(() => {
    if (accessToken && me.data && !user) setUser(me.data)
  }, [accessToken, me.data, setUser, user])

  useEffect(() => {
    if (me.isError && me.fetchStatus === 'idle' && accessToken && !user) {
      handleSessionExpired()
    }
  }, [accessToken, me.fetchStatus, me.isError, user])

  return <>{children}</>
}
